<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Digite sua mensagem aqui...',
  },
  templateVariables: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showTemplateVariables: {
    type: Boolean,
    default: false,
  },
  showEmojiPicker: {
    type: Boolean,
    default: false,
  },
  hasAudioAttachment: {
    type: Boolean,
    default: false,
  },
  hasSelectedVariations: {
    type: Boolean,
    default: false,
  },
  hasMessengerInbox: {
    type: Boolean,
    default: false,
  },
  showFlagPreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'toggle-variables',
  'toggle-emoji',
  'attach-file',
  'generate-ai-variations',
]);

const editorRef = ref(null);
const emojiButton = ref(null);
const variablesButton = ref(null);

// Local state for dropdowns
const localShowTemplateVariables = ref(false);
const localShowEmojiPicker = ref(false);

const message = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const variableItems = computed(() =>
  props.templateVariables.map(variable => ({
    label: variable.name,
    key: variable.name,
    description: variable.description,
  }))
);

// Check if AI generation can be used (requires at least 20 characters)
const canGenerateWithAI = computed(() => {
  return (
    !props.hasAudioAttachment &&
    props.modelValue &&
    props.modelValue.trim().length >= 20
  );
});

// Handle generate variations button click
const handleGenerateVariations = () => {
  // Always emit the event, let parent component handle validation
  emit('generate-ai-variations');
};

// WhatsApp formatting functions
const formatText = type => {
  const formats = {
    bold: { prefix: '**', suffix: '**', placeholder: 'texto' },
    italic: { prefix: '_', suffix: '_', placeholder: 'texto' },
    strikethrough: { prefix: '~', suffix: '~', placeholder: 'texto' },
    code: { prefix: '`', suffix: '`', placeholder: 'codigo' },
  };
  const format = formats[type];
  if (!format) return;

  editorRef.value?.wrapSelection(format);
};

// Keyboard shortcuts
const handleEditorKeydown = event => {
  // Block all special keyboard shortcuts when audio is attached
  if (props.hasAudioAttachment) {
    return true;
  }

  // WhatsApp formatting shortcuts
  if (event.ctrlKey || event.metaKey) {
    switch (event.key.toLowerCase()) {
      case 'b':
        event.preventDefault();
        formatText('bold');
        return true;
      case 'i':
        event.preventDefault();
        formatText('italic');
        return true;
      case 'u':
        event.preventDefault();
        formatText('strikethrough');
        return true;
      case 'e':
        event.preventDefault();
        formatText('code');
        return true;
    }
  }

  return false;
};

// Handle template variables toggle
const toggleTemplateVariables = () => {
  localShowTemplateVariables.value = !localShowTemplateVariables.value;
  localShowEmojiPicker.value = false; // Close emoji picker

  // Prevent body scroll when dropdown is open
  if (localShowTemplateVariables.value) {
    // Save current scroll position
    const scrollY = window.scrollY;

    // Set body styles to prevent scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  } else {
    // Restore scroll position
    const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

    // Reset body styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore scroll position
    if (scrollY > 0) {
      window.scrollTo(0, scrollY);
    }
  }

  emit('toggle-variables');
};

// Handle emoji picker toggle
const toggleEmojiPicker = () => {
  localShowEmojiPicker.value = !localShowEmojiPicker.value;
  localShowTemplateVariables.value = false; // Close variables dropdown

  // Prevent body scroll when picker is open
  if (localShowEmojiPicker.value) {
    // Save current scroll position
    const scrollY = window.scrollY;

    // Set body styles to prevent scroll
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  } else {
    // Restore scroll position
    const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

    // Reset body styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore scroll position
    if (scrollY > 0) {
      window.scrollTo(0, scrollY);
    }
  }

  emit('toggle-emoji');
};

// Insert variable into text
const insertVariable = variableName => {
  editorRef.value?.insertTextAtSelection(variableName);

  // Close dropdown and restore scroll
  if (localShowTemplateVariables.value) {
    localShowTemplateVariables.value = false;

    // Restore scroll position
    const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

    // Reset body styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore scroll position
    if (scrollY > 0) {
      window.scrollTo(0, scrollY);
    }
  }

};

// Insert emoji into text
const insertEmoji = emoji => {
  editorRef.value?.insertTextAtSelection(emoji);

  // Close picker and restore scroll
  if (localShowEmojiPicker.value) {
    localShowEmojiPicker.value = false;

    // Restore scroll position
    const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

    // Reset body styles
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore scroll position
    if (scrollY > 0) {
      window.scrollTo(0, scrollY);
    }
  }

};

// Computed property for emoji picker positioning
const emojiPickerPosition = computed(() => {
  if (!emojiButton.value || !localShowEmojiPicker.value) return {};

  const buttonRect = emojiButton.value.$el.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  const pickerHeight = 300;
  const pickerWidth = 320;

  const spaceBelow = windowHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;
  const shouldOpenUpward = spaceBelow < pickerHeight && spaceAbove > spaceBelow;

  const spaceRight = windowWidth - buttonRect.right;
  const shouldOpenLeftward = spaceRight < pickerWidth;

  let top;
  let left;

  if (shouldOpenUpward) {
    top = `${buttonRect.top - pickerHeight - 4}px`;
  } else {
    top = `${buttonRect.bottom + 4}px`;
  }

  if (shouldOpenLeftward) {
    left = `${buttonRect.left - pickerWidth + buttonRect.width}px`;
  } else {
    left = `${buttonRect.left}px`;
  }

  return { top, left };
});

// Computed property for template variables dropdown positioning
const templateVariablesPosition = computed(() => {
  if (!variablesButton.value || !localShowTemplateVariables.value) return {};

  const buttonRect = variablesButton.value.$el.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  // Estimated dropdown dimensions
  const dropdownHeight = 300; // max-h-60 is approximately 240px + padding
  const dropdownWidth = 288; // w-72 = 18rem = 288px

  const spaceBelow = windowHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;
  const shouldOpenUpward =
    spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  const spaceRight = windowWidth - buttonRect.left;
  const shouldOpenLeftward = spaceRight < dropdownWidth;

  let top;
  let left;

  if (shouldOpenUpward) {
    // Open upward
    top = `${Math.max(10, buttonRect.top - dropdownHeight - 4)}px`;
  } else {
    // Open downward
    top = `${buttonRect.bottom + 4}px`;
  }

  if (shouldOpenLeftward) {
    // Align to right edge of button
    left = `${Math.max(10, buttonRect.right - dropdownWidth)}px`;
  } else {
    // Align to left edge of button
    left = `${buttonRect.left}px`;
  }

  // Calculate max height based on available space
  const maxHeight = shouldOpenUpward
    ? Math.min(dropdownHeight, buttonRect.top - 20)
    : Math.min(dropdownHeight, windowHeight - buttonRect.bottom - 20);

  return {
    top,
    left,
    maxHeight: `${maxHeight}px`,
  };
});

// Click outside to close dropdowns
const handleClickOutside = event => {
  if (!event.target.closest('.whatsapp-editor')) {
    // Handle template variables dropdown
    if (
      !event.target.closest('.template-variables-dropdown') &&
      localShowTemplateVariables.value
    ) {
      localShowTemplateVariables.value = false;

      // Restore scroll position
      const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

      // Reset body styles
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';

      // Restore scroll position
      if (scrollY > 0) {
        window.scrollTo(0, scrollY);
      }
    }

    // Handle emoji picker dropdown
    if (
      !event.target.closest('.emoji-picker-dropdown') &&
      localShowEmojiPicker.value
    ) {
      localShowEmojiPicker.value = false;

      // Restore scroll position
      const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

      // Reset body styles
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';

      // Restore scroll position
      if (scrollY > 0) {
        window.scrollTo(0, scrollY);
      }
    }
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);

  // Ensure complete cleanup on unmount
  const scrollY = Math.abs(parseInt(document.body.style.top || '0'));

  // Reset body styles
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  document.body.style.overflow = '';

  // Restore scroll position if needed
  if (scrollY > 0) {
    window.scrollTo(0, scrollY);
  }
});
</script>

<template>
  <div class="whatsapp-editor">
    <!-- Formatting Toolbar -->
    <div class="toolbar flex items-center gap-2 p-2">
      <Button
        v-if="!hasMessengerInbox"
        v-tooltip="
          hasAudioAttachment ? 'Não disponível com áudio' : 'Negrito (Ctrl+B)'
        "
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-bold"
        :disabled="hasAudioAttachment"
        @click="formatText('bold')"
      />

      <Button
        v-if="!hasMessengerInbox"
        v-tooltip="
          hasAudioAttachment ? 'Não disponível com áudio' : 'Itálico (Ctrl+I)'
        "
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-italic"
        :disabled="hasAudioAttachment"
        @click="formatText('italic')"
      />

      <Button
        v-if="!hasMessengerInbox"
        v-tooltip="
          hasAudioAttachment ? 'Não disponível com áudio' : 'Riscado (Ctrl+U)'
        "
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-strikethrough"
        :disabled="hasAudioAttachment"
        @click="formatText('strikethrough')"
      />

      <Button
        v-if="!hasMessengerInbox"
        v-tooltip="
          hasAudioAttachment ? 'Não disponível com áudio' : 'Código (Ctrl+E)'
        "
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-code"
        :disabled="hasAudioAttachment"
        @click="formatText('code')"
      />

      <!-- Utility Buttons -->
      <div class="relative inline-block">
        <Button
          ref="emojiButton"
          v-tooltip="'Emojis'"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-smile"
          :disabled="hasAudioAttachment"
          @click="toggleEmojiPicker"
        />

        <!-- Emoji Picker Dropdown -->
        <div
          v-if="localShowEmojiPicker"
          class="emoji-picker-dropdown fixed z-50 bg-n-background dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-7 rounded-lg shadow-lg"
          :style="emojiPickerPosition"
        >
          <EmojiInput :on-click="emoji => insertEmoji(emoji)" />
        </div>
      </div>

      <Button
        v-if="!hasMessengerInbox"
        v-tooltip="'Anexar arquivo'"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-paperclip"
        label="Anexos"
        @click="emit('attach-file')"
      />

      <div class="relative inline-block">
        <Button
          ref="variablesButton"
          v-tooltip="'Variáveis'"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-braces"
          label="Variáveis"
          :disabled="hasAudioAttachment"
          @click="toggleTemplateVariables"
        />

        <!-- Template Variables Dropdown -->
        <Teleport to="body">
          <div
            v-if="localShowTemplateVariables"
            class="template-variables-dropdown fixed z-50 w-72 overflow-y-auto bg-n-background dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-7 rounded-lg shadow-xl"
            :style="{
              top: templateVariablesPosition.top,
              left: templateVariablesPosition.left,
              maxHeight: templateVariablesPosition.maxHeight,
            }"
          >
            <div
              class="p-2 text-xs text-n-slate-11 dark:text-n-slate-11 border-b border-n-slate-3 dark:border-n-slate-7 sticky top-0 bg-n-background dark:bg-n-slate-1"
            >
              Use as variáveis abaixo para personalizar sua mensagem para cada
              contato
            </div>
            <div class="divide-y divide-n-slate-3">
              <div
                v-for="variable in templateVariables"
                :key="variable.name"
                class="p-2 hover:bg-n-slate-1 dark:hover:bg-n-slate-2 cursor-pointer flex justify-between"
                @click="insertVariable(variable.name)"
              >
                <span class="font-mono text-n-brand dark:text-n-brand">{{
                  variable.name
                }}</span>
                <span class="text-xs text-n-slate-11 dark:text-n-slate-11">{{
                  variable.description
                }}</span>
              </div>
            </div>
          </div>
        </Teleport>
      </div>

      <div class="relative inline-flex items-center">
        <button
          v-tooltip="
            hasSelectedVariations
              ? 'Ver variações selecionadas'
              : hasAudioAttachment
                ? 'Não disponível com áudio'
                : 'Gerar variações com IA'
          "
          :class="[
            'relative px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap overflow-hidden',
            hasAudioAttachment
              ? 'bg-n-slate-3 text-n-slate-12 cursor-not-allowed'
              : hasSelectedVariations
                ? 'ai-button ai-button-no-hover text-white disabled:opacity-50 disabled:cursor-not-allowed'
                : 'ai-button text-white hover:scale-105 disabled:scale-100 disabled:opacity-50 disabled:cursor-not-allowed',
          ]"
          :disabled="hasAudioAttachment && !hasSelectedVariations"
          @click="handleGenerateVariations"
        >
          <span class="relative flex items-center gap-2 z-10">
            <i
              :class="
                hasSelectedVariations
                  ? 'i-lucide-list text-base'
                  : 'i-lucide-sparkles text-base'
              "
            />
            <span class="whitespace-nowrap">{{
              hasSelectedVariations ? 'Ver variações' : 'Gerar variações'
            }}</span>
          </span>
        </button>
      </div>
    </div>

    <!-- Main Editor Area -->
    <div class="editor-content relative">
      <WootMessageEditor
        ref="editorRef"
        v-model="message"
        :placeholder="placeholder"
        :disabled="disabled || hasAudioAttachment"
        class="whatsapp-editor__input [&_.ProseMirror-menubar]:!hidden"
        :class="{ 'opacity-50 cursor-not-allowed': hasAudioAttachment }"
        enable-variables
        :enable-canned-responses="false"
        :variable-items="variableItems"
        :custom-keydown-handler="handleEditorKeydown"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.whatsapp-editor__input {
  ::v-deep {
    .ProseMirror-menubar-wrapper {
      @apply flex flex-col;
    }

    .ProseMirror-menubar-wrapper > .ProseMirror {
      padding: 0.75rem;
      background: transparent;
    }

    .ProseMirror-woot-style {
      min-height: 150px;
      max-height: 400px;
      overflow-y: auto;
    }
  }
}

.ai-button {
  background: #1f93ff;
  position: relative;
}

.ai-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(147, 197, 253, 0.3) 30%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(147, 197, 253, 0.3) 70%,
    transparent 100%
  );
  transform: skewX(-25deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-button:not(.ai-button-no-hover):hover {
  filter: brightness(115%);
}

.ai-button:not(.ai-button-no-hover):hover::before {
  opacity: 1;
  animation: shine-sweep 1.2s ease;
}

@keyframes shine-sweep {
  0% {
    left: -150%;
  }
  100% {
    left: 150%;
  }
}

.ai-button:disabled {
  background: #9ca3af;
  filter: none;
}

.ai-button:disabled::before {
  display: none;
}
</style>
